// Bracket shapes offered by the Generate Bracket dialog. The server builds the
// tree from `type`, so these must match the maps in bracket.service.js:
// - sections: Last32 / Last16 / Last8 / Last4
// - teams: Last64 / Last32 / Last16 / Last8 / Last4 (Last2 is not supported)
// `initialMatches` is the size of the first round; `requiredSections` is the
// exact section count that fits the shape. Draws between two standard sizes
// use the play-offs shape below, which the dialog sends as a resolved `rounds`
// tree rather than a `type`.

export const SECTION_BRACKET_TYPES = [
  {
    value: 'Last32',
    label: 'Last 32',
    requiredSections: 32,
    initialMatches: 16,
    rounds: [
      { name: 'Last 32', matches: 16 },
      { name: 'Last 16', matches: 8 },
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    value: 'Last16',
    label: 'Last 16',
    requiredSections: 16,
    initialMatches: 8,
    rounds: [
      { name: 'Last 16', matches: 8 },
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    value: 'Last8',
    label: 'Last 8',
    requiredSections: 8,
    initialMatches: 4,
    rounds: [
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    value: 'Last4',
    label: 'Last 4',
    requiredSections: 4,
    initialMatches: 2,
    rounds: [
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  }
];

export const TEAM_BRACKET_TYPES = [
  {
    value: 'Last64',
    label: 'Last 64',
    capacity: 64,
    initialMatches: 32,
    rounds: [
      { name: 'Last 64', matches: 32 },
      { name: 'Last 32', matches: 16 },
      { name: 'Last 16', matches: 8 },
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    value: 'Last32',
    label: 'Last 32',
    capacity: 32,
    initialMatches: 16,
    rounds: [
      { name: 'Last 32', matches: 16 },
      { name: 'Last 16', matches: 8 },
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    value: 'Last16',
    label: 'Last 16',
    capacity: 16,
    initialMatches: 8,
    rounds: [
      { name: 'Last 16', matches: 8 },
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    value: 'Last8',
    label: 'Last 8',
    capacity: 8,
    initialMatches: 4,
    rounds: [
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    value: 'Last4',
    label: 'Last 4',
    capacity: 4,
    initialMatches: 2,
    rounds: [
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  }
];

export const findSectionType = (value, sectionCount) => {
  if (value === PLAYOFFS_TYPE) {
    return playoffSectionType(sectionCount);
  }
  return SECTION_BRACKET_TYPES.find((type) => type.value === value) || null;
};

export const findTeamType = (value) =>
  TEAM_BRACKET_TYPES.find((type) => type.value === value) || null;

// The only standard size that covers the sections exactly. A draw with more
// sections than a standard size uses the play-offs shape below.
export const defaultSectionType = (sectionCount) => {
  const match = SECTION_BRACKET_TYPES.find((type) => type.requiredSections === sectionCount);
  if (match) {
    return match.value;
  }
  return playoffSectionType(sectionCount) ? PLAYOFFS_TYPE : '';
};

// The smallest bracket that holds every team (12 teams -> Last 16), so a new
// bracket does not leave teams out by default.
export const defaultTeamType = (teamCount) => {
  if (!teamCount) {
    return '';
  }

  const fitting = TEAM_BRACKET_TYPES.filter((type) => type.capacity >= teamCount).sort(
    (a, b) => a.capacity - b.capacity
  );

  return fitting[0] ? fitting[0].value : '';
};

// ---------------------------------------------------------------- play-offs
// A draw with more sections than the next standard round can hold uses a
// preliminary round: the surplus sections play off and those winners join the
// sections that fit straight into the stage. 21 sections -> 5 play-offs and 11
// direct places in the Last 16.
export const PLAYOFFS_TYPE = 'Playoffs';

const PLAYOFF_STAGES = [
  {
    size: 4,
    label: 'Last 4',
    roundName: 'Semi-finals',
    rounds: [
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    size: 8,
    label: 'Last 8',
    roundName: 'Quarter-finals',
    rounds: [
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    size: 16,
    label: 'Last 16',
    roundName: 'Last 16',
    rounds: [
      { name: 'Last 16', matches: 8 },
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  },
  {
    size: 32,
    label: 'Last 32',
    roundName: 'Last 32',
    rounds: [
      { name: 'Last 32', matches: 16 },
      { name: 'Last 16', matches: 8 },
      { name: 'Quarter-finals', matches: 4 },
      { name: 'Semi-finals', matches: 2 },
      { name: 'Final', matches: 1 }
    ]
  }
];

export const playoffSectionType = (sectionCount) => {
  const stage = PLAYOFF_STAGES.find(
    (entry) => sectionCount > entry.size && sectionCount < entry.size * 2
  );
  if (!stage) {
    return null;
  }

  const initialMatches = sectionCount - stage.size;

  return {
    value: PLAYOFFS_TYPE,
    label: `Play-offs → ${stage.label}`,
    playoffs: true,
    stage: stage.size,
    stageLabel: stage.roundName,
    requiredSections: sectionCount,
    initialMatches,
    directSections: stage.size * 2 - sectionCount,
    rounds: [{ name: 'Play-offs', matches: initialMatches }, ...stage.rounds]
  };
};

// Cards for the setup step: the play-offs shape (when one fits) first, then
// every standard size, disabled unless the event has exactly that many
// sections.
export const sectionTypeOptions = (sectionCount) => {
  const options = SECTION_BRACKET_TYPES.map((type) => ({
    type,
    disabled: sectionCount !== type.requiredSections
  }));
  const playoffs = playoffSectionType(sectionCount);
  if (playoffs) {
    options.unshift({ type: playoffs, disabled: false });
  }
  return options;
};
